/**
 * Saudi HR defaults — labor law, GOSI/SANED/WPS, Nitaqat, currency.
 */
import { getBrandDefaultCurrency, getBrandCurrencySuffix } from './brandCurrency';
import {
  GOSI_SAUDI_EMPLOYER_RATE,
  GOSI_SAUDI_EMPLOYEE_RATE,
  GOSI_EXPAT_EMPLOYER_RATE,
  SANED_EMPLOYER_RATE,
  SANED_EMPLOYEE_RATE,
  calculateGOSIContributions,
  calculateSANEDContributions,
  calculateEOSBProvision,
  calculateSaudiPayrollSummary,
  isSaudiNational,
} from '../../pages/salaires/utils/payrollSaudiFormat';

export {
  GOSI_SAUDI_EMPLOYER_RATE,
  GOSI_SAUDI_EMPLOYEE_RATE,
  GOSI_EXPAT_EMPLOYER_RATE,
  SANED_EMPLOYER_RATE,
  SANED_EMPLOYEE_RATE,
  calculateGOSIContributions,
  calculateSANEDContributions,
  calculateEOSBProvision,
  calculateSaudiPayrollSummary,
  isSaudiNational,
};

/** Default currency for HR module (SAR). */
export const HR_DEFAULT_CURRENCY = getBrandDefaultCurrency();

/** Currency suffix for labels and chart axes. */
export const HR_CURRENCY_SUFFIX = getBrandCurrencySuffix();

/** Saudi labor law — probation period in days (Art. 53). */
export const SAUDI_PROBATION_DAYS = 90;

/** Annual leave minimum (Art. 109). */
export const SAUDI_ANNUAL_LEAVE_DAYS = 21;

/** Sick leave maximum per year (Art. 117). */
export const SAUDI_SICK_LEAVE_DAYS = 120;

/** Standard notice period (days). */
export const SAUDI_NOTICE_PERIOD_DAYS = 30;

/** Max weekly working hours (Art. 98). */
export const SAUDI_MAX_WEEKLY_HOURS = 48;

/** Default Nitaqat Saudization target (%). */
export const SAUDI_NITAQAT_TARGET_PCT = 30;

/** Nitaqat band thresholds (simplified — actual bands vary by activity/size). */
export const NITAQAT_BANDS = {
  green: { min: 40, label: 'أخضر', color: '#22c55e' },
  yellow: { min: 26, max: 39, label: 'أصفر', color: '#eab308' },
  red: { max: 25, label: 'أحمر', color: '#ef4444' },
};

/** Default HR module configuration (Saudi). */
export const SAUDI_HR_CONFIG = {
  currency: HR_DEFAULT_CURRENCY,
  probationPeriod: SAUDI_PROBATION_DAYS,
  probationUnit: 'days',
  noticePeriod: SAUDI_NOTICE_PERIOD_DAYS,
  workingHoursPerWeek: SAUDI_MAX_WEEKLY_HOURS,
  annualLeaveDays: SAUDI_ANNUAL_LEAVE_DAYS,
  sickLeaveDays: SAUDI_SICK_LEAVE_DAYS,
  annualLeaveAccrual: 'monthly',
  maxConsecutiveLeaveDays: 30,
  minimumLeaveNotice: 7,
  leaveApprovalLevels: 2,
  carryForwardLeave: true,
  maxCarryForwardDays: 30,
  gosiEnabled: true,
  gosiSaudiEmployerRate: GOSI_SAUDI_EMPLOYER_RATE,
  gosiSaudiEmployeeRate: GOSI_SAUDI_EMPLOYEE_RATE,
  gosiExpatEmployerRate: GOSI_EXPAT_EMPLOYER_RATE,
  sanedEnabled: true,
  sanedEmployerRate: SANED_EMPLOYER_RATE,
  sanedEmployeeRate: SANED_EMPLOYEE_RATE,
  wpsEnabled: true,
  wpsDeadlineDay: 28,
  eosbAccrualEnabled: true,
  nitaqatEnabled: true,
  nitaqatTargetPct: SAUDI_NITAQAT_TARGET_PCT,
  nitaqatActivityCode: '',
  salaryAlertThresholdSAR: 15000,
  iqamaExpiryAlertDays: 60,
  workPermitExpiryAlertDays: 60,
  medicalInsuranceExpiryAlertDays: 30,
  contractExpiryAlertDays: 30,
  autoPostJobs: false,
  requireMultipleInterviews: true,
  backgroundCheckRequired: true,
  probationReviewMandatory: true,
  mandatoryTrainingHours: 40,
  trackCertifications: true,
  certificationExpiryAlerts: true,
  alertDaysBeforeExpiry: 30,
  annualReviewRequired: true,
  reviewMonth: 12,
  selfEvaluationEnabled: true,
  managerEvaluationRequired: true,
  contractTemplates: true,
  autoGenerateContracts: false,
  digitalSignatures: true,
  documentRetentionYears: 10,
  notifyOnLeaveRequest: true,
  notifyOnContractExpiry: true,
  notifyOnCertificateExpiry: true,
  notifyOnIqamaExpiry: true,
  notifyOnWorkPermitExpiry: true,
  notifyOnMedicalInsuranceExpiry: true,
  notifyOnNitaqatBandChange: true,
  emailNotifications: true,
  restrictDocumentAccess: true,
  requireApprovalForChanges: true,
  auditTrail: true,
  defaultView: 'table',
  showOrgChart: true,
  itemsPerPage: 50,
  employeeReferencePrefix: 'EMP',
  leaveReferencePrefix: 'LEV',
  contractReferencePrefix: 'CON',
  sanctionReferencePrefix: 'SAN',
  departureReferencePrefix: 'DEP',
  evaluationReferencePrefix: 'EVA',
  trainingReferencePrefix: 'TRN',
  recruitmentReferencePrefix: 'REC',
  accidentReferencePrefix: 'ACC',
};

/**
 * Infer Nitaqat band from Saudization percentage.
 * @param {number} saudiPct — % of Saudi nationals in workforce
 */
export function getNitaqatBand(saudiPct) {
  const pct = Number(saudiPct) || 0;
  if (pct >= NITAQAT_BANDS.green.min) return 'green';
  if (pct >= (NITAQAT_BANDS.yellow.min ?? 26)) return 'yellow';
  return 'red';
}

/** Estimated monthly payroll cost for an employee (gross + employer GOSI/SANED + EOSB). */
export function estimateMonthlyEmployerCost(employee, grossSalary) {
  const summary = calculateSaudiPayrollSummary(employee, grossSalary);
  return (
    summary.salaireBrut +
    summary.retenueGOSIEmployeur +
    summary.retenueSANEDEmployeur +
    summary.provisionEOSB
  );
}

/** Turnover replacement cost estimate (typically 3–6 months salary — use 4 months). */
export function estimateTurnoverCost(avgMonthlyGross) {
  const avg = Number(avgMonthlyGross) || 0;
  return Math.round(avg * 4 * 100) / 100;
}

/** Format ar-SA date for reports and alerts. */
export function formatHrDate(dateInput, locale = 'ar-SA') {
  if (!dateInput) return '';
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return String(dateInput);
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Detect Saudi vs expat from HR employee row (DB or API shape). */
export function isHrEmployeeSaudi(emp = {}) {
  return isSaudiNational({
    nationality: emp.nationality || emp.nationalite,
    isSaudi: emp.isSaudi ?? emp.estSaudi,
    isExpat: emp.isExpat ?? emp.estEtranger,
    country: emp.country || emp.pays,
    securite_sociale: emp.securite_sociale ?? emp.securiteSociale,
    passeport: emp.passeport,
  });
}

/** Compute Saudization stats from employee list. */
export function computeSaudizationStats(employees = []) {
  const active = employees.filter((e) => (e.status || 'active') === 'active');
  const saudi = active.filter(isHrEmployeeSaudi).length;
  const expat = active.length - saudi;
  const total = active.length;
  const saudiPct = total > 0 ? Math.round((saudi / total) * 1000) / 10 : 0;
  const band = getNitaqatBand(saudiPct);
  const target = SAUDI_NITAQAT_TARGET_PCT;
  const gap = Math.max(0, Math.ceil((target / 100) * total - saudi));

  return {
    total,
    saudi,
    expat,
    saudiPct,
    expatPct: total > 0 ? Math.round((expat / total) * 1000) / 10 : 0,
    nitaqatBand: band,
    nitaqatTargetPct: target,
    hiringGap: gap,
    compliant: saudiPct >= target,
  };
}
